// src/components/quiz/QuizFeedback.jsx
import React, { useEffect, useState } from 'react';
import { CheckCircle2, XCircle, Sparkles, Zap, Award } from 'lucide-react';

export default function QuizFeedback({ 
  feedback, 
  currentQ, 
  onContinue, 
  streak,
  xpGained,
  activeSkin 
}) {
  // ✅ PRIMERO LA VALIDACIÓN, ANTES DE CUALQUIER HOOK
  if (!feedback) return null;
  
  // ✅ AHORA SÍ LOS HOOKS (siempre en el mismo orden)
  const [showConfetti, setShowConfetti] = useState(false);
  const isCorrect = feedback === 'correct';

  // Efecto de confetti para respuestas correctas
  useEffect(() => {
    if (isCorrect) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [isCorrect]);
  
  // Mensajes motivacionales variables
  const correctMessages = [
    '¡EXCELENTE!',
    '¡PERFECTO!',
    '¡INCREÍBLE!',
    '¡GENIAL!',
    '¡BRAVO!',
    '¡LO LOGRASTE!'
  ];

  const wrongMessages = [
    'INCORRECTO',
    'NO ES ESA',
    'CASI...',
    'INTENTA DE NUEVO'
  ];

  const displayMessage = isCorrect 
    ? correctMessages[Math.floor(Math.random() * correctMessages.length)]
    : wrongMessages[Math.floor(Math.random() * wrongMessages.length)];

  return (
    <>
      {/* OVERLAY SEMI-TRANSPARENTE */}
      <div 
        className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[99] animate-in fade-in duration-300"
        onClick={onContinue}
      />

      {/* PANEL DE FEEDBACK */}
      <div className={`fixed bottom-0 left-0 right-0 p-4 md:p-6 rounded-t-[2.5rem] shadow-2xl z-[100] animate-in slide-in-from-bottom duration-500 ${
        isCorrect 
          ? 'bg-gradient-to-br from-green-100 via-green-50 to-emerald-100' 
          : 'bg-gradient-to-br from-red-100 via-red-50 to-rose-100'
      }`}>
        
        {/* CONFETTI EFFECT */}
        {showConfetti && isCorrect && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute w-2 h-2 bg-yellow-400 rounded-full animate-confetti"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: '-10px',
                  animationDelay: `${Math.random() * 0.5}s`,
                  animationDuration: `${1 + Math.random()}s`
                }}
              />
            ))}
          </div>
        )}

        <div className="max-w-2xl mx-auto relative">
          
          {/* HEADER CON ICONO Y MENSAJE */}
          <div className="flex items-start gap-3 md:gap-4 mb-4 md:mb-6">
            {/* Icono animado */}
            <div className={`p-3 md:p-4 rounded-2xl shrink-0 shadow-lg animate-in zoom-in duration-300 ${
              isCorrect 
                ? 'bg-gradient-to-br from-green-500 to-emerald-600 text-white' 
                : 'bg-gradient-to-br from-red-500 to-rose-600 text-white'
            }`}>
              {isCorrect ? (
                <CheckCircle2 size={32} className="animate-bounce" />
              ) : (
                <XCircle size={32} className="animate-shake" />
              )}
            </div>

            {/* Mensaje principal */}
            <div className="flex-1 pt-1">
              <h3 className={`font-black text-2xl md:text-3xl mb-2 animate-in slide-in-from-left duration-300 ${
                isCorrect ? 'text-green-800' : 'text-red-800'
              }`}>
                {displayMessage}
              </h3>

              {/* Respuesta correcta si falló */}
              {!isCorrect && currentQ.a && (
                <div className="animate-in fade-in slide-in-from-left-4 duration-500 delay-150">
                  <p className="text-red-700 font-bold text-sm md:text-base mb-2">
                    Respuesta correcta:
                  </p>
                  <div className="bg-white/80 backdrop-blur-sm px-4 py-3 rounded-xl border-2 border-red-200 shadow-md inline-block">
                    <span className="text-red-900 font-black text-lg md:text-xl">
                      {currentQ.a}
                    </span>
                  </div>
                </div>
              )}

              {/* Stats si es correcto */}
              {isCorrect && (
                <div className="flex flex-wrap gap-3 mt-3 animate-in fade-in slide-in-from-left-4 duration-500 delay-150">
                  {/* Racha */}
                  {streak > 0 && (
                    <div className="flex items-center gap-1.5 bg-orange-100 border-2 border-orange-300 px-3 py-1.5 rounded-full shadow-sm">
                      <Zap size={14} className="text-orange-600" />
                      <span className="text-xs font-black text-orange-700">
                        {streak} RACHA
                      </span>
                    </div>
                  )}

                  {/* XP Ganado */}
                  {xpGained > 0 && (
                    <div className="flex items-center gap-1.5 bg-indigo-100 border-2 border-indigo-300 px-3 py-1.5 rounded-full shadow-sm">
                      <Sparkles size={14} className="text-indigo-600" />
                      <span className="text-xs font-black text-indigo-700">
                        +{xpGained} XP
                      </span>
                    </div>
                  )}

                  {/* Perfect */}
                  {streak >= 3 && (
                    <div className="flex items-center gap-1.5 bg-yellow-100 border-2 border-yellow-300 px-3 py-1.5 rounded-full shadow-sm animate-pulse">
                      <Award size={14} className="text-yellow-600" />
                      <span className="text-xs font-black text-yellow-700">
                        PERFECT!
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* BOTÓN CONTINUAR MEJORADO */}
          <button 
            onClick={onContinue} 
            className={`w-full py-4 md:py-5 rounded-2xl font-black text-lg md:text-xl border-b-8 transition-all active:border-b-0 active:translate-y-2 shadow-xl flex items-center justify-center gap-3 animate-in zoom-in duration-300 delay-300 ${
              isCorrect 
                ? 'bg-gradient-to-r from-green-500 to-emerald-600 border-green-800 text-white hover:from-green-400 hover:to-emerald-500' 
                : 'bg-gradient-to-r from-red-500 to-rose-600 border-red-800 text-white hover:from-red-400 hover:to-rose-500'
            }`}
          >
            CONTINUAR
            {isCorrect && <CheckCircle2 size={24} />}
          </button>

          {/* Hint de atajo de teclado */}
          <p className="text-center mt-3 text-xs font-bold text-slate-400 animate-in fade-in duration-500 delay-500">
            Presiona ENTER o toca el botón
          </p>
        </div>
      </div>

      {/* ESTILOS ADICIONALES */}
      <style jsx>{`
        @keyframes confetti {
          0% { 
            transform: translateY(0) rotate(0deg); 
            opacity: 1;
          }
          100% { 
            transform: translateY(100vh) rotate(720deg); 
            opacity: 0;
          }
        }
        
        @keyframes shake {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(-10deg); }
          75% { transform: rotate(10deg); }
        }
        
        .animate-confetti {
          animation: confetti 1.5s ease-out forwards;
        }
        
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </>
  );
}
